import kebabCase from 'lodash/kebabCase';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import useSWR from 'swr';

import { SearchTitle, SearchBoxButton } from 'search/styles/SearchStyles';
import { RecentSearchesCache } from 'search/utils/RecentSearchesCache';
import { Loader } from 'shared/components/Loader';
import { requestService } from 'shared/utils/services/requestService';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { t } from 'localization/utils/t';
import { startCase } from 'shared/utils/startCase';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { FeatureFlags } from 'featureFlags/constants';

const TP = 'search.components.search.LatestSearches';

const fetchLatestSearches = async () => {
  return requestService({
    url: '/web-api/v1/search-metadata/latest-searches',
    method: 'GET',
  });
};

export const LatestSearches = () => {
  const router = useRouter();
  const { data, error } = useSWR('latest-searches', fetchLatestSearches);
  const latestSearches = data?.searches?.slice(0, 10) || [];

  const isConsumerSearchEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_SEARCH_MICROFRONTEND);

  const handleClick = (latestSearch: string) => {
    RecentSearchesCache.add(latestSearch);
    sendTrackingEvent('SEARCH_LATEST_TAP', {
      search_string: latestSearch,
      service_name: isConsumerSearchEnabled ? 'consumer_search' : 'constructor',
    });
    void router.push(`/search?query=${latestSearch}&pageNumber=1`); // Use router to navigate to the search results page
  };

  if (error || latestSearches.length === 0) {
    return null;
  }

  if (!latestSearches) {
    return <Loader style={{ margin: '30px 0' }} />;
  }

  return (
    <LatestSearches.Wrapper>
      <LatestSearches.Title>{t(`${TP}.latestSearches`, 'Latest searches')}</LatestSearches.Title>
      {latestSearches.map((value) => (
        <SearchBoxButton key={kebabCase(value)} onClick={() => handleClick(startCase(value))}>
          {startCase(value, { capitalizeInOtherLanguages: false })}
        </SearchBoxButton>
      ))}
    </LatestSearches.Wrapper>
  );
};

LatestSearches.Wrapper = styled.div`
  margin-bottom: 24px;
`;

LatestSearches.Title = styled(SearchTitle)`
  ${SearchTitle};
  margin-bottom: 16px;
`;
